import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';

const RestaurantOrder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [menuLoading, setMenuLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [tableNumber, setTableNumber] = useState('');
  const [tableCode, setTableCode] = useState('');
  const [tableVerified, setTableVerified] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [specialInstructions, setSpecialInstructions] = useState('');

  useEffect(() => {
    if (tableVerified) {
      fetchMenu();
    }
  }, [tableVerified]);

  const verifyTable = async () => {
    if (!tableNumber || !tableCode) {
      setMessage({ type: 'error', text: 'Please enter both table number and table code' });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('restaurant_tables')
        .select('*')
        .eq('table_number', parseInt(tableNumber))
        .eq('table_code', tableCode.toUpperCase())
        .single();

      if (error || !data) {
        setMessage({ type: 'error', text: 'Invalid table number or code. Please check and try again.' });
        return;
      }

      setTableVerified(true);
      setMessage({ type: 'success', text: `Welcome to Table ${tableNumber}!` });
    } catch (error) {
      console.error('Error verifying table:', error);
      setMessage({ type: 'error', text: 'Failed to verify table' });
    } finally {
      setLoading(false);
    }
  };

  const fetchMenu = async () => {
    try {
      // Fetch categories
      const { data: categoriesData, error: catError } = await supabase
        .from('menu_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (catError) throw catError;
      setCategories(categoriesData || []);

      // Fetch menu items
      const { data: itemsData, error: itemsError } = await supabase
        .from('menu_items')
        .select(`
          *,
          category:menu_categories(name)
        `)
        .eq('is_available', true)
        .order('display_order');

      if (itemsError) throw itemsError;
      setMenuItems(itemsData || []);
    } catch (error) {
      console.error('Error fetching menu:', error);
      setMessage({ type: 'error', text: 'Failed to load menu' });
    } finally {
      setMenuLoading(false);
    }
  };

  const addToCart = (item) => {
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
    } else {
      setCart([...cart, { ...item, quantity: 1, itemInstructions: '' }]);
    }
    
    setMessage({ type: 'success', text: `${item.name} added to cart` });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      setCart(cart.filter(item => item.id !== itemId));
    } else {
      setCart(cart.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const updateItemInstructions = (itemId, instructions) => {
    setCart(cart.map(item =>
      item.id === itemId ? { ...item, itemInstructions: instructions } : item
    ));
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2);
  };

  const submitOrder = async () => {
    if (!user) {
      setMessage({ type: 'error', text: 'Please login to place an order' });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    if (cart.length === 0) {
      setMessage({ type: 'error', text: 'Your cart is empty' });
      return;
    }

    setLoading(true);
    try {
      // Generate order number
      const orderNumber = `ORD-${Date.now()}`;

      // Create order
      const { data: orderData, error: orderError } = await supabase
        .from('restaurant_orders')
        .insert([{
          user_id: user.id,
          table_number: parseInt(tableNumber),
          table_code: tableCode.toUpperCase(),
          order_number: orderNumber,
          total_amount: calculateTotal(),
          status: 'pending',
          special_instructions: specialInstructions
        }])
        .select()
        .single();

      if (orderError) throw orderError;

      // Create order items
      const orderItems = cart.map(item => ({
        order_id: orderData.id,
        menu_item_id: item.id,
        quantity: item.quantity,
        price: item.price,
        special_instructions: item.itemInstructions,
        status: 'pending'
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      // Create notification for kitchen
      await supabase
        .from('order_notifications')
        .insert([{
          order_id: orderData.id,
          sender_type: 'customer',
          message: `New order from Table ${tableNumber} - Order #${orderNumber}`,
          is_read: false
        }]);

      setMessage({ 
        type: 'success', 
        text: `Order placed successfully! Order #${orderNumber}. The kitchen will start preparing your order.` 
      });
      
      setCart([]);
      setSpecialInstructions('');
      
      setTimeout(() => {
        navigate('/restaurant-orders');
      }, 3000);

    } catch (error) {
      console.error('Error submitting order:', error);
      setMessage({ type: 'error', text: 'Failed to submit order. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const filteredMenuItems = selectedCategory === 'all'
    ? menuItems
    : menuItems.filter(item => item.category_id === selectedCategory);

  if (!tableVerified) {
    return (
      <div className="page-container">
        <div className="page-wrapper">
          <button 
            onClick={() => navigate('/prive-lounge')} 
            className="back-button"
            style={{
              marginBottom: '1rem',
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--gold)',
              color: 'var(--gold)',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>

          <h1 className="page-title">Order from Your Table</h1>
          <div className="underline"></div>
          <p className="page-subtitle">Enter your table details to start ordering</p>

          {message.text && (
            <div className={`message ${message.type}`} style={{
              padding: '1rem',
              marginBottom: '1rem',
              borderRadius: '8px',
              background: message.type === 'success' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
              color: message.type === 'success' ? '#00ff00' : '#ff0000',
              border: `1px solid ${message.type === 'success' ? '#00ff00' : '#ff0000'}`
            }}>
              {message.text}
            </div>
          )}

          <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                Table Number
              </label>
              <input
                type="number"
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                placeholder="e.g., 5"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid var(--gold)',
                  borderRadius: '4px',
                  color: '#000'
                }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                Table Code
              </label>
              <input
                type="text"
                value={tableCode}
                onChange={(e) => setTableCode(e.target.value.toUpperCase())}
                placeholder="e.g., TBL005"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid var(--gold)',
                  borderRadius: '4px',
                  color: '#000',
                  textTransform: 'uppercase'
                }}
              />
              <small style={{ color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.5rem', display: 'block' }}>
                You'll find the table code on your table card
              </small>
            </div>

            <button
              onClick={verifyTable}
              disabled={loading}
              className="cta-button"
              style={{
                width: '100%',
                padding: '1rem',
                background: 'var(--gold)',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? <LoadingSpinner /> : 'Verify Table'}
            </button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <button 
            onClick={() => setTableVerified(false)} 
            className="back-button"
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--gold)',
              color: 'var(--gold)',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Change Table
          </button>
          
          <div style={{ 
            background: 'var(--gold)', 
            color: 'black', 
            padding: '0.5rem 1rem', 
            borderRadius: '4px',
            fontWeight: 'bold'
          }}>
            Table {tableNumber}
          </div>
        </div>

        <h1 className="page-title">Menu</h1>
        <div className="underline"></div>

        {message.text && (
          <div className={`message ${message.type}`} style={{
            padding: '1rem',
            marginBottom: '1rem',
            borderRadius: '8px',
            background: message.type === 'success' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
            color: message.type === 'success' ? '#00ff00' : '#ff0000',
            border: `1px solid ${message.type === 'success' ? '#00ff00' : '#ff0000'}`
          }}>
            {message.text}
          </div>
        )}

        {/* Category Filter */}
        <div style={{ marginBottom: '2rem', overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <button
            onClick={() => setSelectedCategory('all')}
            style={{
              padding: '0.5rem 1rem',
              margin: '0.25rem',
              background: selectedCategory === 'all' ? 'var(--gold)' : 'transparent',
              color: selectedCategory === 'all' ? 'black' : 'var(--gold)',
              border: '1px solid var(--gold)',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            All
          </button>
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              style={{
                padding: '0.5rem 1rem',
                margin: '0.25rem',
                background: selectedCategory === category.id ? 'var(--gold)' : 'transparent',
                color: selectedCategory === category.id ? 'black' : 'var(--gold)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {category.name}
            </button>
          ))}
        </div>

        {menuLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* Menu Items */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              {filteredMenuItems.map(item => (
                <div key={item.id} className="card" style={{ padding: '1rem' }}>
                  {item.image_url && (
                    <img 
                      src={item.image_url} 
                      alt={item.name}
                      style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }}
                    />
                  )}
                  <h3 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>{item.name}</h3>
                  <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem', opacity: 0.8 }}>{item.description}</p>
                  <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--gold)', marginBottom: '0.5rem' }}>
                    £{item.price.toFixed(2)}
                  </p>
                  {item.preparation_time && (
                    <p style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '0.5rem' }}>
                      ⏱ {item.preparation_time} mins
                    </p>
                  )}
                  {item.dietary_info && item.dietary_info.length > 0 && (
                    <p style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '0.5rem' }}>
                      {item.dietary_info.join(', ')}
                    </p>
                  )}
                  <button
                    onClick={() => addToCart(item)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'var(--gold)',
                      color: 'black',
                      border: 'none',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Add to Cart
                  </button>
                </div>
              ))}
            </div>

            {/* Cart */}
            {cart.length > 0 && (
              <div className="card" style={{ position: 'sticky', bottom: '80px', background: 'rgba(0, 0, 0, 0.95)' }}>
                <h2 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Your Cart ({cart.length} items)</h2>
                
                {cart.map(item => (
                  <div key={item.id} style={{ 
                    padding: '1rem', 
                    marginBottom: '1rem', 
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <h4>{item.name}</h4>
                      <span style={{ color: 'var(--gold)' }}>£{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        style={{
                          padding: '0.25rem 0.75rem',
                          background: 'var(--gold)',
                          color: 'black',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        style={{
                          padding: '0.25rem 0.75rem',
                          background: 'var(--gold)',
                          color: 'black',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        +
                      </button>
                      <button
                        onClick={() => updateQuantity(item.id, 0)}
                        style={{
                          padding: '0.25rem 0.75rem',
                          background: 'red',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginLeft: 'auto'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    
                    <input
                      type="text"
                      placeholder="Special instructions for this item..."
                      value={item.itemInstructions}
                      onChange={(e) => updateItemInstructions(item.id, e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid rgba(205, 182, 136, 0.5)',
                        borderRadius: '4px',
                        color: '#000',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                ))}

                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                    Special Instructions for Entire Order
                  </label>
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Any allergies or special requests?"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid var(--gold)',
                      borderRadius: '4px',
                      color: '#000',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '1rem 0',
                  borderTop: '1px solid var(--gold)',
                  marginTop: '1rem'
                }}>
                  <h3 style={{ color: 'var(--gold)' }}>Total:</h3>
                  <h3 style={{ color: 'var(--gold)' }}>£{calculateTotal()}</h3>
                </div>

                <button
                  onClick={submitOrder}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: 'var(--gold)',
                    color: 'black',
                    border: 'none',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? <LoadingSpinner /> : 'Place Order'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default RestaurantOrder;
